import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  USAGE_FILTERS,
  USAGE_FILTER_LABELS,
  type AccountUsage,
  type UsageFilter,
} from '@azko/shared';

import { api } from '../../lib/api.js';
import {
  Card,
  CardHeader,
  EmptyState,
  LoadingRows,
  PageHeader,
  StatusBadge,
  cx,
} from '../../components/ui.js';

/**
 * Uso contra limite — a tela que o dado ja permitia e ninguem tinha.
 *
 * Todo numero aqui ja existia no banco: `usage_counters` conta o consumo do
 * mes, `plan_entitlements` diz o teto, `subscription_overrides` diz a excecao.
 * O que nao existia era alguem perguntando. O resultado e que as tres unicas
 * perguntas comerciais que esses numeros respondem ficavam sem resposta:
 *
 *   PERTO DO TETO  — e o gatilho de upgrade. Ligar aqui e vender; esperar o
 *                    cliente ligar e atender reclamacao.
 *   ESTOUROU       — nao e relatorio, e incidente: o porteiro esta vendo 402 no
 *                    balcao agora, com a caixa na mao.
 *   PARADA         — conta que nao registrou nenhuma encomenda no mes. E o
 *                    sinal mais barato de churn que este produto tem.
 *
 * Abre em "perto do teto" porque e a unica das tres em que agir cedo muda o
 * desfecho — nas outras duas o estrago ja aconteceu.
 */

function BarraDeUso({ pct }: { pct: number }) {
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-pill bg-line">
      <div
        className={cx(
          'h-full rounded-pill',
          pct >= 100 ? 'bg-warn' : pct >= 80 ? 'bg-accent' : 'bg-muted',
        )}
        // Passa de 100% com frequencia (a cota e cobrada no registro, e a
        // excecao pode ter sido baixada depois). A barra para em 100 para nao
        // vazar da caixa; o numero ao lado conta a verdade.
        style={{ width: `${Math.min(pct, 100)}%` }}
      />
    </div>
  );
}

function ContaEmUso({ conta }: { conta: AccountUsage }) {
  return (
    <li className="rounded-2xl border border-line bg-surface-2 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="min-w-0">
          <span className="block truncate font-bold">{conta.name}</span>
          <span className="block truncate text-sm text-faint">
            {conta.planName ?? 'sem plano'}
          </span>
        </span>

        <span className="flex shrink-0 items-center gap-2">
          {conta.idle && <StatusBadge tone="neutral">parada no mes</StatusBadge>}
          {conta.worstPct >= 100 && <StatusBadge tone="warn">estourou</StatusBadge>}
          {conta.worstPct >= 80 && conta.worstPct < 100 && (
            <StatusBadge tone="accent">{conta.worstPct}% do teto</StatusBadge>
          )}
        </span>
      </div>

      {conta.items.length === 0 ? (
        <p className="mt-3 text-sm text-faint">
          O plano desta conta nao tem limite numerico em nada — nao ha teto contra o que medir.
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {conta.items.map((item) => (
            <li key={item.key}>
              <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                <span className="flex items-center gap-2">
                  {item.label}
                  {/* Saber que o teto veio de excecao muda a conversa: nao da
                      para "vender mais limite" a quem a equipe acabou de
                      liberar mais limite de cortesia. */}
                  {item.fromOverride && (
                    <span className="rounded-pill border border-accent/40 px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-wider text-accent">
                      excecao
                    </span>
                  )}
                </span>
                <span
                  className={cx(
                    'tabular-nums',
                    item.pct >= 100 ? 'font-bold text-warn' : 'text-muted',
                  )}
                >
                  {item.used} de {item.limit} ({item.pct}%)
                </span>
              </div>
              <div className="mt-1">
                <BarraDeUso pct={item.pct} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

const DESCRICOES: Record<UsageFilter, string> = {
  all: 'Toda conta ativa com algum limite numerico no plano.',
  near: 'Passaram de 80% de algum limite. E aqui que se vende upgrade — antes de o cliente esbarrar.',
  over: 'Ja bateram o teto. Nestas contas alguem esta sendo barrado agora, com a caixa na mao no balcao.',
  idle: 'Nenhuma encomenda registrada neste mes. O sinal mais barato de churn que este produto tem.',
};

export function UsagePage() {
  const [filtro, setFiltro] = useState<UsageFilter>('near');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'uso', filtro],
    queryFn: () =>
      api.get<{ items: AccountUsage[]; total: number }>(`/admin/uso?filtro=${filtro}`),
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <>
      <PageHeader title="Uso e limites" />

      <Card>
        <CardHeader
          title="Consumo contra o teto do plano"
          subtitle="Quem esta perto de estourar, quem ja estourou, e quem parou de usar"
        />

        <div className="space-y-3 px-5 pb-5 sm:px-6">
          <div className="flex flex-wrap gap-2">
            {USAGE_FILTERS.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setFiltro(option)}
                className={
                  filtro === option
                    ? 'rounded-pill border border-accent bg-accent/10 px-3 py-1.5 text-sm font-semibold text-accent'
                    : 'rounded-pill border border-line bg-surface-2 px-3 py-1.5 text-sm font-semibold text-muted transition hover:text-fg'
                }
              >
                {USAGE_FILTER_LABELS[option]}
              </button>
            ))}
          </div>

          <p className="text-sm text-muted">{DESCRICOES[filtro]}</p>

          {/* Isto e um RANKING, nao um feed: a lista sai por quem esta mais
              perto de bater primeiro, e quem abre a tela quer o topo. Por isso
              nao ha paginacao — mas o total precisa aparecer, porque "14 perto
              do teto" e a resposta comercial mesmo quando so 50 cabem na tela. */}
          {total > 0 && (
            <p className="text-sm text-faint">
              {total} {total === 1 ? 'conta' : 'contas'}
              {items.length < total && ` • mostrando as ${items.length} mais criticas`}
            </p>
          )}
        </div>

        {isLoading && <LoadingRows rows={4} />}

        {!isLoading && items.length === 0 && (
          <EmptyState
            title="Nenhuma conta aqui"
            description={
              filtro === 'over'
                ? 'Ninguem esbarrando no limite do plano agora.'
                : filtro === 'near'
                  ? 'Nenhuma conta passou de 80% do teto neste mes.'
                  : filtro === 'idle'
                    ? 'Toda conta ativa registrou ao menos uma encomenda neste mes.'
                    : 'Nenhuma conta ativa tem limite numerico para medir.'
            }
          />
        )}

        {items.length > 0 && (
          <ul className="space-y-3 border-t border-line-soft p-4 sm:p-5">
            {items.map((conta) => (
              <ContaEmUso key={conta.id} conta={conta} />
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}
